import * as React from 'react';
import { Component } from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import moment from 'moment';
import Lazy from 'lazy.js';
import { History, Location } from 'history';
import { AppUser } from '../../../App/App';
import { SchoolEvent } from '../../../../models/event';
import { School } from '../../../../models/school';
import { DEFAULT_RANGE_CALENDAR_YEARS, CALENDAR_FILTER } from '../../../../consts/calendar';
import { getYearRangeArray } from '../../../../helpers/calendar/calendar';
import { Loader } from '../../../../components/Loader/Loader';
import { ItemsBar } from './ItemsBar';
import { DaysOfWeekBar } from './DaysOfWeekBar';
import { MonthDaysPanel } from './MonthDaysPanel';
import { EventAndTournamentsTable } from './EventAndTournamentsTable';
import { CalendarFilterButtons } from './CalendarFilterButtons';
import { MonthYearSelector } from './MonthYearSelector';
import './Calendar.scss';
import { getStudentCalendarData, StudentCalendarData } from '../../../../helpers/service/student/events';
import { StudentPageHeader } from '../components/StudentPageHeader';
import '../components/StudentHub.scss';

export type FilterType = 'SPORT_EVENTS' | 'CLUB_EVENTS' | 'TOURNAMENT_EVENTS' | 'ALL';
type CalendarDateType = 'fixture' | 'club' | 'tournamentEvent';
type CalendarDateTypes = Record<string, CalendarDateType[]>;

function getCalendarDateKey(value: string | Date): string {
  const date = new Date(value);
  return date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate();
}

function addCalendarDateTypes(map: CalendarDateTypes, values: string[], type: CalendarDateType): void {
  values.forEach(value => {
    const key = getCalendarDateKey(value);
    const current = map[key] || [];
    current.push(type);
    map[key] = current;
  });
}

function buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates) {
  const dates = [...fixtureEventDates.dates, ...clubEventDates.dates, ...tournamentEventDates.dates];
  const eventDateTypes: CalendarDateTypes = {};

  addCalendarDateTypes(eventDateTypes, fixtureEventDates.dates, 'fixture');
  addCalendarDateTypes(eventDateTypes, clubEventDates.dates, 'club');
  addCalendarDateTypes(eventDateTypes, tournamentEventDates.dates, 'tournamentEvent');

  const datesUniq: string[] = Lazy(dates)
    .uniq()
    .toArray();

  return {
    dates: datesUniq.map(date => new Date(date)),
    eventDateTypes
  };
}

function buildCalendarState(data: StudentCalendarData) {
  return {
    ...buildCalendarDateState(data.fixtureEventDates, data.clubEventDates, data.tournamentEventDates),
    fixtureEvents: data.fixtureEvents,
    clubEvents: data.clubEvents,
    tournamentEvents: data.tournamentEvents
  };
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  monthAndYearDate: Date;
  selectedDate: Date;
  nowDate: Date;
  fixtureEvents: SchoolEvent[];
  clubEvents: SchoolEvent[];
  tournamentEvents: SchoolEvent[];
  dates: Date[];
  eventDateTypes: CalendarDateTypes;
  activeMonthIndex: number;
  activeYearIndex: number;
  isLoading: boolean;
  filter: FilterType;
  schools: School[];
  schoolIdList: string[];
}

export class StudentCalendar extends Component<Props, State> {
  constructor(props) {
    super(props);

    const now = new Date();
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    const monthAndYearDate = new Date(nowYear, nowMonth, 1);

    this.state = {
      monthAndYearDate,
      selectedDate: now,
      nowDate: now,
      activeMonthIndex: nowMonth,
      activeYearIndex: DEFAULT_RANGE_CALENDAR_YEARS,
      fixtureEvents: [],
      clubEvents: [],
      tournamentEvents: [],
      dates: [],
      eventDateTypes: {},
      isLoading: false,
      filter: CALENDAR_FILTER.ALL as FilterType,
      schools: [],
      schoolIdList: []
    };
  }

  getEventPromises(filter: FilterType, monthAndYearDate: Date, selectedDate: Date, schoolIdList: string[]) {
    return [getStudentCalendarData(this.props.user, monthAndYearDate, selectedDate, schoolIdList, filter)];
  }

  componentDidMount() {
    const { monthAndYearDate, selectedDate, filter } = this.state;
    const { location } = this.props;
    const { state } = location;

    const filterFromHistory = propz.get(state, ['filter']);
    const selectedDateFromHistory = propz.get(state, ['selectedDate']);
    const isFilterFromHistoryExist = typeof filterFromHistory !== 'undefined';
    const isSelectedDateFromHistoryExist = typeof selectedDateFromHistory !== 'undefined';

    const currentFilter = isFilterFromHistoryExist ? filterFromHistory : filter;
    const currentDate = isSelectedDateFromHistoryExist ? selectedDateFromHistory : selectedDate;

    const now = new Date(currentDate);
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    const currentMonthAndYearDate = new Date(nowYear, nowMonth, 1);

    this.setState({
      isLoading: true
    });

    const promises = this.getEventPromises(currentFilter, currentMonthAndYearDate, now, []);
    BPromise.all(promises)
      .then(([data]: [StudentCalendarData]) => {
        const calendarDateState = buildCalendarState(data);
        const schools = data.schools as School[];
        this.setState({
          isLoading: false,
          ...calendarDateState,
          schools,
          schoolIdList: schools.map(school => school.id),
          filter: currentFilter,
          selectedDate: now,
          monthAndYearDate: currentMonthAndYearDate,
          activeMonthIndex: nowMonth
        });
      })
      .catch(() => this.setState({ isLoading: false }));
  }

  onNextMonthClick = (): void => {
    const { monthAndYearDate, activeYearIndex, filter, schoolIdList } = this.state;
    const nowMonth = monthAndYearDate.getMonth();
    const nextMonth = new Date(monthAndYearDate);
    nextMonth.setMonth(nowMonth + 1);
    const nextMonthIndex = nextMonth.getMonth();
    const nextSelectedDate = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);

    const promises = this.getEventPromises(filter, nextMonth, nextSelectedDate, schoolIdList);

    this.setState({
      isLoading: true
    });

    if (nowMonth === 11) {
      if (activeYearIndex === DEFAULT_RANGE_CALENDAR_YEARS * 2) {
        this.setState({
          isLoading: false
        });
        return;
      }

      BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
        const calendarDateState = buildCalendarState(data);
        this.setState({
          isLoading: false,
          ...calendarDateState,
          selectedDate: nextSelectedDate,
          monthAndYearDate: nextMonth,
          activeMonthIndex: nextMonthIndex,
          activeYearIndex: activeYearIndex + 1
        });
      });
    } else {
      BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
        const calendarDateState = buildCalendarState(data);
        this.setState({
          isLoading: false,
          ...calendarDateState,
          selectedDate: nextSelectedDate,
          monthAndYearDate: nextMonth,
          activeMonthIndex: nextMonthIndex
        });
      });
    }
  };
  onPreviousMonthClick = (): void => {
    const { monthAndYearDate, activeYearIndex, filter, schoolIdList } = this.state;
    const nowMonth = monthAndYearDate.getMonth();
    const previousMonth = new Date(monthAndYearDate);
    previousMonth.setMonth(nowMonth - 1);
    const previousMonthIndex = previousMonth.getMonth();
    const nextSelectedDate = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), 1);

    const promises = this.getEventPromises(filter, previousMonth, nextSelectedDate, schoolIdList);

    this.setState({
      isLoading: true
    });

    if (nowMonth === 0) {
      if (activeYearIndex === 0) {
        this.setState({
          isLoading: false
        });
        return;
      }

      BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
        const calendarDateState = buildCalendarState(data);
        this.setState({
          isLoading: false,
          ...calendarDateState,
          selectedDate: nextSelectedDate,
          monthAndYearDate: previousMonth,
          activeMonthIndex: previousMonthIndex,
          activeYearIndex: activeYearIndex - 1
        });
      });
    } else {
      BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
        const calendarDateState = buildCalendarState(data);
        this.setState({
          isLoading: false,
          ...calendarDateState,
          selectedDate: nextSelectedDate,
          monthAndYearDate: previousMonth,
          activeMonthIndex: previousMonthIndex
        });
      });
    }
  };

  onMonthYearChange = (month: number, year: number): void => {
    const { filter, schoolIdList } = this.state;
    const nextMonth = new Date(year, month, 1);
    const promises = this.getEventPromises(filter, nextMonth, nextMonth, schoolIdList);
    const activeYearIndex = getYearRangeArray().findIndex(option => option === year);

    this.setState({ isLoading: true });

    BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
      const calendarDateState = buildCalendarState(data);
      this.setState({
        isLoading: false,
        ...calendarDateState,
        selectedDate: nextMonth,
        monthAndYearDate: nextMonth,
        activeMonthIndex: month,
        activeYearIndex
      });
    });
  };

  onMonthChange = (value: number): void => {
    const { monthAndYearDate, filter, schoolIdList } = this.state;
    const nextMonth = new Date(monthAndYearDate);
    nextMonth.setMonth(value);
    const nextSelectedDate = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);

    const promises = this.getEventPromises(filter, nextMonth, nextSelectedDate, schoolIdList);

    this.setState({
      isLoading: true
    });

    BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
      const calendarDateState = buildCalendarState(data);
      this.setState({
        isLoading: false,
        ...calendarDateState,
        selectedDate: nextSelectedDate,
        monthAndYearDate: nextMonth,
        activeMonthIndex: Number(value)
      });
    });
  };

  onYearChange = (value: number): void => {
    const { monthAndYearDate, filter, schoolIdList } = this.state;
    const nextYear = new Date(monthAndYearDate);
    nextYear.setFullYear(value);
    const nextSelectedDate = new Date(nextYear.getFullYear(), nextYear.getMonth(), 1);

    const promises = this.getEventPromises(filter, nextYear, nextSelectedDate, schoolIdList);

    const yearRangeArray = getYearRangeArray();
    const activeYearIndex = yearRangeArray.findIndex(year => year === Number(value));

    this.setState({
      isLoading: true
    });

    BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
      const calendarDateState = buildCalendarState(data);
      this.setState({
        isLoading: false,
        ...calendarDateState,
        selectedDate: nextSelectedDate,
        monthAndYearDate: nextYear,
        activeYearIndex: activeYearIndex
      });
    });
  };

  onDayClick = (date: Date): void => {
    const { filter, monthAndYearDate, schoolIdList } = this.state;
    this.setState({
      isLoading: true
    });

    const promises = this.getEventPromises(filter, monthAndYearDate, date, schoolIdList);

    BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
      const calendarDateState = buildCalendarState(data);
      this.setState({
        selectedDate: date,
        isLoading: false,
        ...calendarDateState
      });
    });
  };

  onFilterButtonClick = (filter: FilterType) => {
    const { monthAndYearDate, selectedDate, schoolIdList } = this.state;

    this.setState({
      isLoading: true
    });

    const promises = this.getEventPromises(filter, monthAndYearDate, selectedDate, schoolIdList);

    BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
      const calendarDateState = buildCalendarState(data);
      this.setState({
        isLoading: false,
        ...calendarDateState,
        filter
      });
    });
  };

  onChildClick = (schoolId: string) => {
    this.setState({
      isLoading: true
    });

    let schoolIdList;

    if (schoolId === 'all') {
      const { schools } = this.state;
      schoolIdList = schools.map(school => school.id);
    } else {
      schoolIdList = [schoolId];
    }

    const { filter, monthAndYearDate, selectedDate } = this.state;

    const promises = this.getEventPromises(filter, monthAndYearDate, selectedDate, schoolIdList);

    BPromise.all(promises).then(([data]: [StudentCalendarData]) => {
      const calendarDateState = buildCalendarState(data);
      this.setState({
        isLoading: false,
        ...calendarDateState,
        filter,
        schoolIdList
      });
    });
  };

  onEventClick = (eventId: string): void => {
    const { filter } = this.state;
    const { location } = this.props;
    const { pathname } = location;

    this.props.history.push({
      pathname: '/events/event',
      search: `id=${eventId}`,
      state: {
        filter,
        prevRoutePath: pathname
      }
    });
  };

  getSchoolName = (school: School) => {
    return school.name;
  };

  render() {
    const {
      activeMonthIndex,
      activeYearIndex,
      selectedDate,
      monthAndYearDate,
      nowDate,
      isLoading,
      fixtureEvents,
      clubEvents,
      tournamentEvents,
      dates,
      eventDateTypes,
      filter,
      schools,
      schoolIdList
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const { user } = this.props;

    const events = [...fixtureEvents, ...clubEvents, ...tournamentEvents];
    const eventsSorted = events.sort((event1, event2) => {
      const eventStartTimeDate1 = new Date(event1.startTime);
      const eventStartTimeDate2 = new Date(event2.startTime);
      return Number(eventStartTimeDate1) - Number(eventStartTimeDate2);
    });
    const selectedDateTitle = moment(selectedDate).format('D MMMM YYYY');
    const selectedDateRelative = moment(selectedDate).isSame(nowDate, 'day')
      ? 'Today'
      : moment(selectedDate).format('dddd');
    const selectedItemsCount = eventsSorted.length;
    return (
      <main className="parentPage studentHubPage studentCalendarPage">
        <StudentPageHeader
          title="Calendar"
          chips={[
            {
              value: selectedItemsCount,
              label: selectedItemsCount === 1 ? 'event on selected day' : 'events on selected day',
              tone: selectedItemsCount ? 'amber' : 'slate'
            },
            { label: moment(monthAndYearDate).format('MMMM YYYY'), tone: 'blue' }
          ]}
        />

        <div className="cal-screen">
          <ItemsBar
            items={schools}
            allItemsTitle={'All schools'}
            itemIdList={schoolIdList}
            onClick={this.onChildClick}
            getItemName={this.getSchoolName}
          />

          <CalendarFilterButtons user={user} filter={filter} onFilterButtonClick={this.onFilterButtonClick} />

          <div className="cal">
            <section className="cal-card cal-card--month">
              <MonthYearSelector
                activeMonthIndex={activeMonthIndex}
                activeYearIndex={activeYearIndex}
                onPreviousMonthClick={this.onPreviousMonthClick}
                onNextMonthClick={this.onNextMonthClick}
                onMonthYearChange={this.onMonthYearChange}
              />
              <DaysOfWeekBar />
              <MonthDaysPanel
                onClick={this.onDayClick}
                selectedDate={selectedDate}
                monthAndYearDate={monthAndYearDate}
                nowDate={nowDate}
                eventDates={dates}
                eventDateTypes={eventDateTypes}
              />
            </section>

            <section className="cal-card cal-card--events">
              <div className="cal-day__hd">
                <div>
                  <h2 className="cal-day__date">{selectedDateTitle}</h2>
                  <span className="cal-day__rel">· {selectedDateRelative}</span>
                </div>
                {selectedItemsCount > 0 ? (
                  <span className="cal-day__count">
                    {selectedItemsCount} event{selectedItemsCount === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>
              <div className="eEventAndTournamentsTableSection">
                <EventAndTournamentsTable user={user} events={eventsSorted} onEventClick={this.onEventClick} />
              </div>
            </section>
          </div>
        </div>
      </main>
    );
  }
}
